import type { Contract } from './types';

import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { zodContractSchema } from './validations';

/**
 * Fetches a single contract by ID from the API with all line items.
 *
 * @param contractId - The UUID of the contract to fetch.
 * @param options - Additional request options.
 * @returns The contract data including all line items.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/get_contract_contracts__contract_id__get
 */
export const fetchContract = async (
  contractId: UUID,
  options?: KyRequestOptions,
) => {
  const response = await vestigasApi
    .get(ENDPOINT.CONTRACT.GET(contractId), {
      ...options,
    })
    .json<Contract | undefined>();

  // Validate the response with Zod
  const validatedResponse = zodContractSchema.safeParse(response);
  if (validatedResponse?.error?.issues?.length) {
    log.error({
      message: 'Contract API response has validation errors',
      error: {
        errors: validatedResponse?.error?.issues,
        errorMessage: validatedResponse?.error?.message,
        response,
      },
    });
  }

  return response;
};
